import { LGraphNode, LiteGraph } from "litegraph.js";
import { addDrivableSlider, finalizeDrivableSliders, syncDrivableSliders } from "../utils/addDrivableSlider.js";

/**
 * ImageInputNode
 * --------------
 * Load an image file as a canvas source.
 * Click to open file picker, or drag-and-drop.
 */
export class ImageInputNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Image Input";

    this.properties = { src: "", imageWidth: 0, offsetY: 0, canvasWidth: 0, canvasHeight: 0 };

    this._image = null;
    this._outCanvas = document.createElement("canvas");
    this._outCtx = this._outCanvas.getContext("2d");
    this._fileName = "No image loaded";
    this._dirty = false;

    this.addOutput("Canvas", "canvas");

    addDrivableSlider(this, "Size", "imageWidth", 0, 10, 1000, 1, () => {
      this._redraw();
    });

    finalizeDrivableSliders(this, 0);

    this.size = [200, 160];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  // Load from a URL (e.g., a preset's default asset)
  loadFromUrl(url) {
    this.properties.src = url;
    // For SVGs, fetch as text and inject explicit dimensions so the browser
    // can rasterize them. SVGs with width="100%" have no intrinsic size.
    if (url.endsWith('.svg')) {
      fetch(url)
        .then(r => r.text())
        .then(svgText => {
          // Inject explicit 512x512 on the root svg element
          const fixed = svgText.replace(
            /<svg([^>]*)>/,
            (m, attrs) => {
              let a = attrs.replace(/\s(width|height)="[^"]*"/g, '');
              return `<svg${a} width="512" height="512" style="color:#000">`;
            }
          );
          const blob = new Blob([fixed], { type: 'image/svg+xml' });
          const dataUrl = URL.createObjectURL(blob);
          this._loadSrc(dataUrl, url.split('/').pop());
        })
        .catch(e => console.warn('[ImageInput] SVG fetch failed:', url, e));
    } else {
      this._loadSrc(url, url.split('/').pop());
    }
  }

  _redraw() {
    if (!this._image) return;
    const p = this.properties;
    const natW = this._image.naturalWidth || 300;
    const natH = this._image.naturalHeight || 300;
    const aspect = natH / natW;

    const cw = p.canvasWidth || natW;
    const ch = p.canvasHeight || natH;
    this._outCanvas.width = cw;
    this._outCanvas.height = ch;
    this._outCtx.clearRect(0, 0, cw, ch);

    if (p.canvasWidth) {
      const iw = p.imageWidth || natW;
      const ih = iw * aspect;
      const x = (cw - iw) / 2;
      const y = p.offsetY != null ? p.offsetY : (ch - ih) / 2;
      this._outCtx.drawImage(this._image, x, y, iw, ih);
    } else {
      this._outCtx.drawImage(this._image, 0, 0, cw, ch);
    }
    this._dirty = true;
    this.setDirtyCanvas(true);
  }

  onDrawForeground(ctx) {
    const w = this.size[0];
    const h = this.size[1];
    const pad = 10;
    const previewY = 56;
    const previewH = h - previewY - 20;

    // Preview area
    ctx.fillStyle = "rgba(0,0,0,0.25)";
    ctx.fillRect(pad, previewY, w - pad * 2, previewH);

    if (this._image && this._image.naturalWidth) {
      const aspect = this._image.naturalWidth / this._image.naturalHeight;
      const pw = w - pad * 2;
      let tw = pw, th = previewH;
      if (aspect > pw / previewH) {
        th = pw / aspect;
      } else {
        tw = previewH * aspect;
      }
      ctx.drawImage(
        this._image,
        pad + (pw - tw) / 2,
        previewY + (previewH - th) / 2,
        tw, th
      );
    } else {
      ctx.fillStyle = "rgba(255,255,255,0.2)";
      ctx.font = "10px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Click to load image", w / 2, previewY + previewH / 2 + 3);
      ctx.textAlign = "left";
    }

    // File name
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "9px sans-serif";
    let name = this._fileName;
    if (name.length > 28) name = name.substring(0, 28) + "\u2026";
    ctx.fillText(name, pad, h - 8);
  }

  onMouseDown(e, localPos) {
    // Only open file picker when clicking the preview area (below slider, above filename)
    const previewTop = 56;
    const previewBottom = this.size[1] - 20;
    if (localPos[1] > previewTop && localPos[1] < previewBottom) {
      this._openFilePicker();
      return true;
    }
    return false;
  }

  _openFilePicker() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.style.display = "none";
    document.body.appendChild(input);

    input.addEventListener("change", () => {
      if (input.files && input.files[0]) {
        this._loadFile(input.files[0]);
      }
      document.body.removeChild(input);
    });

    input.click();
  }

  _loadFile(file) {
    this._fileName = file.name;
    const reader = new FileReader();
    reader.onload = (e) => {
      this._loadSrc(e.target.result, file.name);
    };
    reader.readAsDataURL(file);
  }

  _loadSrc(src, name) {
    const img = new Image();
    img.onerror = (e) => console.warn('[ImageInput] Failed to load:', src, e);
    img.onload = () => {
      this._image = img;
      this._fileName = name || "image";
      // Set default size from image if not already set via properties
      if (!this.properties.imageWidth) {
        this.properties.imageWidth = img.naturalWidth || 300;
        if (this.widgets) {
          this.widgets.forEach(w => { if (w.name === "Size") w.value = this.properties.imageWidth; });
        }
      }
      if (this.widgets) {
        this.widgets.forEach(w => { if (w.name === "Offset Y") w.value = this.properties.offsetY || 0; });
      }
      this._redraw();
    };
    img.src = src;
  }

  onExecute() {
    const prev = this.properties.imageWidth;
    syncDrivableSliders(this);
    // Redraw if size changed via driven input
    if (this._image && this.properties.imageWidth !== prev) {
      this._redraw();
    }

    if (this._image && this._image.naturalWidth) {
      this.setOutputData(0, this._outCanvas);
    } else {
      this.setOutputData(0, null);
    }
  }
}

LiteGraph.registerNodeType("input/ImageInput", ImageInputNode);
